import {
	Color,
	CzmlDataSource,
	Cartesian3,
	HeightReference,
	Math as CesiumMath,
	PolygonGraphics,
	PolygonHierarchy,
	HeadingPitchRange,
	PolylineDashMaterialProperty,
	JulianDate,
	RequestErrorEvent,
	Entity,
	DataSource
} from 'cesium';
import { handleUnauthorizedResourceFetch, useDataSources } from './cesium-datasources';
import { busyStateMessage, mapSettingsState, useCesiumViewer } from './cesium-viewer';
import { mapModeStore } from './map-mode-store';
import api from '$lib/services/kyAuthenticatedInstance';
import { get } from 'svelte/store';
import type { ISelectedProject } from '$lib/core/map/map-mode-store';
import { HTTPError } from 'ky';
import type { CzmlResponse, IPlanDrawing } from '$lib/models/IProjectModel';
import type { IMastPointMinimalModel } from '$lib/models/IMastPointMinimalModel';
import { createPolygonEntity, createTerrainOffsets, fetchEntitiesInArea, getEntityPropertyBag, processCzmlAndReselectMastPoint, refetchMastPointCZML, setColorProperty, setPartnerColorProperty } from './map-utils';
import { mapToolsStore } from './map-tools-store';
import type { ISupportModel } from '$lib/models/ISupportModel';
import { mapLayersStore } from './map-layers-store';
import type { IAviationObstaclePropertyBagModel } from '$lib/models/IAviationObstacle';
import { ACTIVE_PLAN_COLOR, ENTITY_ID_PREFIX, INACTIVE_PLAN_COLOR } from '$lib/utils/constants';
import { tick } from 'svelte';
import { type ILineDataModel, type ILineDataModelMinimal } from '$lib/models/ILineDataModel';
import { userAccountStore } from '$lib/stores/auth-store';
import { toast } from '$lib/components/organisms/toast/toast';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { hexToRgb } from '$lib/utils/utils';

const showAllLiveObjects = async () => {
	const { mapdataDataSource, aviationObstacleDataSource, distanceAnalysisTerrainProfilesDataSource, distanceAnalysisPolylinesDataSource } = useDataSources();
	const distanceModeEnabled = get(mapToolsStore).mapStoreInputMode === 'distanceMode';

	if (!mapdataDataSource.entities.values.length) return;

	if(distanceModeEnabled){
		// Pre-emptively hide all entities in distance analysis datasources. We do not know which entites are plan or live. Activate live specifically.
		distanceAnalysisPolylinesDataSource.entities.values.forEach((entity) => {
			entity.show = false;
		});
		distanceAnalysisTerrainProfilesDataSource.entities.values.forEach((entity) => {
			entity.show = false;
		});
	}

	mapdataDataSource.entities.values.forEach((entity) => {
		const entityPropertyBag = getEntityPropertyBag(entity);

		if (!entityPropertyBag) return;

		if(distanceModeEnabled && entityPropertyBag && entityPropertyBag.entityType === 'LINE'){
			toggleDistanceModeEntity(entity, true);
		}

		const isVirtualSupport = () => {
			if (entityPropertyBag && entityPropertyBag.entityType === 'SUPPORT') {
				const supportProperties = entityPropertyBag as ISupportModel;
				return supportProperties?.data?.calculated;
			} else {
				return false;
			}
		};
		const isPhysicalSupport = () => {
			if (entityPropertyBag && entityPropertyBag.entityType === 'SUPPORT') {
				const supportProperties = entityPropertyBag as ISupportModel;
				return !supportProperties.data?.calculated;
			} else {
				return false;
			}
		};

		// Checks if supports are toggled on or off, and handles entities accordingly.
		if (
			(isVirtualSupport() &&
				//@ts-ignore
				(!get(mapToolsStore).displayVirtualSupports || entityPropertyBag?.data?.planEntityData)) ||
			(isPhysicalSupport() &&
				//@ts-ignore
				(!get(mapToolsStore).displayPhysicalSupports || entityPropertyBag?.data?.planEntityData))
		) {
			entity.show = false;
			return;
		}

		if (!entity.show) {
			entity.show = true;
		}
	});

	// proccess aviationobstacle datasource
	aviationObstacleDataSource.entities.values.forEach((entity) => {
		const entityPropertyBag = getEntityPropertyBag(entity);

		if (!entityPropertyBag) return;

		const isAviationObstacleRed = () => {
			if (entityPropertyBag.entityType === 'AVIATION_OBSTACLE') {
				const aviationObstaclePropertyBag = entityPropertyBag as IAviationObstaclePropertyBagModel;
				return aviationObstaclePropertyBag.data.highestDistanceFromGround >= 15;
			} else {
				return false;
			}
		};
		const isAviationObstacleBlue = () => {
			if (entityPropertyBag.entityType === 'AVIATION_OBSTACLE') {
				const aviationObstaclePropertyBag = entityPropertyBag as IAviationObstaclePropertyBagModel;
				return aviationObstaclePropertyBag.data.highestDistanceFromGround < 15;
			} else {
				return false;
			}
		};

		// Checks if aviationObstacles are toggled on or off, and handles entities accordingly.
		if (isAviationObstacleRed() && !get(mapLayersStore).aviationObstaclesRedDisplay) {
			entity.show = false;
			return;
		}
		if (isAviationObstacleBlue() && !get(mapLayersStore).aviationObstaclesBlueDisplay) {
			entity.show = false;
			return;
		}
		if (!entity.show) {
			entity.show = true;
		}
	});
};

/**
 * All terrain line entities are related to a line entity. This function will toggle the visibility of the terrain line entities based on the map mode and which lines they belong to.
 * If a plan is showing, we want to show all entities, and hide the live entities. If live is showing, we want to hide all entities and show the live entities.
 * @param liveEntity 
 * @param showDistanceEntities 
 * @returns 
 */
const toggleDistanceModeEntity = async (liveEntity: Entity, showDistanceEntities: boolean) => {
	const { distanceAnalysisPolylinesDataSource, distanceAnalysisTerrainProfilesDataSource } = useDataSources();
	if(!liveEntity)return;

	const terrainProfileEntity = distanceAnalysisTerrainProfilesDataSource.entities.getById('TerrainProfile:' + getEntityPropertyBag<ILineDataModelMinimal>(liveEntity).data.terrainProfileId);
	if(terrainProfileEntity) terrainProfileEntity.show = showDistanceEntities;
	
	const terrainDistanceEntity = distanceAnalysisPolylinesDataSource.entities.getById('LineTerrain:' + getEntityPropertyBag<ILineDataModelMinimal>(liveEntity).data.id);
	if(terrainDistanceEntity){ 
		terrainDistanceEntity.show = showDistanceEntities;
		// @ts-ignore children exists. It's a better way to access instead of the other way around. LineTerrain may have several children which should all inherit visibility.
		terrainDistanceEntity._children.forEach((childEntity: Entity) => childEntity.show = showDistanceEntities)
	}
}

/**
	 * Handles distance polylines and labels when option is changed in a pre-loaded dataset.
	 * A similar logic is used when new areas are loaded.
	 *
	 * @param value
	 */
const handleDistanceDisplay = (value: string) => {
	const { mapdataDataSource, distanceAnalysisPolylinesDataSource } = useDataSources();
	const lineDistanceMode = value as 'all' | 'horizontal' | 'single';
	mapToolsStore.update((store) => {
		store.distanceAnalysisSettings.lineDistanceMode = lineDistanceMode as 'all' | 'horizontal' | 'single';
		return store;
	})
	busyStateMessage.set('Oppdaterer avstandsmålinger...');
	let datasources = [mapdataDataSource];
	const planDatasource = get(mapModeStore).activeDataSource;
	if (planDatasource)
		datasources = [...datasources, planDatasource];
	
	datasources.forEach((datasource) => {
		datasource.entities.values.forEach((entity) => {
			const propertyBag = getEntityPropertyBag<ILineDataModelMinimal>(entity);
			if (!propertyBag || propertyBag.entityType !== 'LINE') return;

			const distanceObject = distanceAnalysisPolylinesDataSource.entities.getById(
				'LineTerrain:' + propertyBag.data.id
			);
			if (!distanceObject) return;

			if (lineDistanceMode === 'all') {
				distanceObject.show = false;
			} else if (
				lineDistanceMode === 'horizontal'
			) {
				distanceObject.show = entity.show;
			} else if (lineDistanceMode === 'single' && propertyBag.data.isLowestOnSpan) {
				distanceObject.show = entity.show;
			} else {
				distanceObject.show = false;
			}
		});
	});

	busyStateMessage.set('');
};

/**
 * Gets an entityCollection which should handle visibility of live data. All references in live data is hidden
 * to prevent showing and interacting with duplicate versions of the "same" entity. e.g: If a live mastpoint is moved,
 * a plan-mastpoint will be created and the live object will be hidden.
 *
 * @param entityCollection An entitycollection to process
 * @param showLiveObjects Default false. Used when hiding a plan from view which should show. True if we want to toggle live objects ON and plan objects OFF.
 *
 * @returns The IDs of the live objects that are shown/hidden.
 */
const handleLiveReferences = async (entityCollection: Entity[]) => {
	// NB: Also handles supports visibility based on deactivated map layer. The reason is that we through this iteration can hook new supports before
	// fully rendered instead of using multiple iterations of all datasources.

	const { mapdataDataSource, distanceAnalysisPolylinesDataSource, distanceAnalysisTerrainProfilesDataSource} = useDataSources();
	const distanceModeEnabled = get(mapToolsStore).mapStoreInputMode === 'distanceMode';

	//A collection of references to live objects (ID) which we will use to hide entities.
	const referenceList: string[] = [];
	//A collection of new plan-supports (ID) which we will use to hide entities.
	let supportsToHide: string[] = [];
	entityCollection
		.forEach((planEntity) => {
			let liveReferenceId: undefined | number = undefined;
			let isMarkedForDeletion = undefined;

			//ENTITYCOLLECTION
			if (planEntity.id.startsWith('sandbox_polygon:')) return;
			if (planEntity.id.startsWith('ProjectPolygon:')) return;

			const entityPropertyBag =
				getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel | ILineDataModel>(planEntity);
			liveReferenceId = entityPropertyBag.data.planEntityData?.liveObjectId;
			const entityType = entityPropertyBag.entityType;
			isMarkedForDeletion = entityPropertyBag.data.planEntityData?.isMarkedForDeletion;
			
			if (isMarkedForDeletion) {
				//In case of deleting a live mastpoint from within a plan, we need to hide the plan version as well.
				//In the future we might mark it with a specified color.
				const planEntityToHide = findEntityByIdBetweenDataSources(planEntity.id);
				if (planEntityToHide) {
					planEntityToHide.show = false;
				}
			}
			
			if (liveReferenceId) {
				switch (entityType) {
                    case 'POLE': 
                        const poleEntityToHide = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.POLE + liveReferenceId);
                        if (!poleEntityToHide) break;
                        referenceList.push(ENTITY_ID_PREFIX.POLE + liveReferenceId);
                        break;
                     case 'UTILITYLINK': 
                        const utilityLinkEntityToHide = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.UTILITYLINK + liveReferenceId);
                        if (!utilityLinkEntityToHide) break;
                        referenceList.push(ENTITY_ID_PREFIX.UTILITYLINK + liveReferenceId);
                        break;
                    case 'CROSSBEAM': 
                        const crossbeamEntityToHide = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.CROSSBEAM + liveReferenceId);
                        if (!crossbeamEntityToHide) break;
                        referenceList.push(ENTITY_ID_PREFIX.CROSSBEAM + liveReferenceId);
                        break;
					case 'MASTPOINT':
						const entityToHide = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.MASTPOINT + liveReferenceId);
						if (!entityToHide) break;

						// Properties of entity to hide (Live)
						const entityProperties =
							getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(entityToHide);
						if (entityProperties.data.poles.length) {
							entityProperties.data.poles.forEach((pole) => {
								//Check for virtual supports connected to the mastpoint
								//Virtual supports are not fetched as a part of czml-data
								const entityTypeSupportPole = mapdataDataSource.entities.getById(
									'supportPoleId:' + pole.id
								);
								if (entityTypeSupportPole) {
									referenceList.push('supportPoleId:' + pole.id);
								}
							});
						}
						referenceList.push(ENTITY_ID_PREFIX.MASTPOINT + liveReferenceId);
						break;
					case 'LINE':
						const lineEntityToHide = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.LINE + liveReferenceId);
						if (!lineEntityToHide) break;

						referenceList.push(ENTITY_ID_PREFIX.LINE + liveReferenceId);

						break;
					case 'SUPPORT':
						// Hides new supports if maplayer is deactivated
						if (
							get(mapToolsStore).displayVirtualSupports == false &&
							planEntity.id.includes('supportPoleId:')
						) {
							supportsToHide.push(planEntity.id);
						}
						if (
							get(mapToolsStore).displayPhysicalSupports == false &&
							planEntity.id.includes(ENTITY_ID_PREFIX.SUPPORT)
						) {
							supportsToHide.push(planEntity.id);
						}
                        if (planEntity.id.includes(ENTITY_ID_PREFIX.SUPPORT)) {
						    //Physical supports of a mastpoint
						    referenceList.push(ENTITY_ID_PREFIX.SUPPORT + liveReferenceId);
                        } else if (planEntity.id.includes(ENTITY_ID_PREFIX.VIRTUAL_SUPPORT)){
						    //Virtual supports of a mastpoint
						    referenceList.push(ENTITY_ID_PREFIX.VIRTUAL_SUPPORT + liveReferenceId);
                        }
						break;
					default:
						break;
				}
			} else {
				return;
			}
		});

	if (supportsToHide.length) {
		supportsToHide.forEach((supportId) => {
			const supportEntity = findEntityByIdBetweenDataSources(supportId);
			if (supportEntity) {
				supportEntity.show = false;
			}
		});
	}

	if(distanceModeEnabled){
		// Pre-emptively show all entities in distance analysis datasources. We do not know which entites are plan or live. Activate live specifically.
		distanceAnalysisPolylinesDataSource.entities.values.forEach((entity) => {
			entity.show = true;
		});
		distanceAnalysisTerrainProfilesDataSource.entities.values.forEach((entity) => {
			entity.show = true;
		});
	}

	referenceList.forEach((id) => {
        let liveReferenceEntity = mapdataDataSource.entities.getById(id)

        // Support can refer to a virtual support which contains another id prefix..
        const entityIdIsSupport = id.startsWith(ENTITY_ID_PREFIX.SUPPORT);
        if(entityIdIsSupport && !liveReferenceEntity){
            // If `support:<id>` is missing, try the corresponding virtual support 
            const alternativeSupportId = id.replace(ENTITY_ID_PREFIX.SUPPORT, ENTITY_ID_PREFIX.VIRTUAL_SUPPORT);
            liveReferenceEntity = mapdataDataSource.entities.getById(alternativeSupportId);
        }

		if (liveReferenceEntity) {
			liveReferenceEntity.show = false;

			if(distanceModeEnabled && id.startsWith(ENTITY_ID_PREFIX.LINE)){
				toggleDistanceModeEntity(liveReferenceEntity, false);
			}

		} else {
			toast({
				type: 'warning',
				title: 'Feil i datagrunnlag',
				description: `ID: ${id} tilhører ingen live-entiteter`
			});
		}
	});

	const {lineDistanceMode, terrainProfileOffsets} =  get(mapToolsStore).distanceAnalysisSettings;
	if(distanceModeEnabled && lineDistanceMode !== 'all'){
		handleDistanceDisplay(lineDistanceMode);
		await createTerrainOffsets();
	}
	if(distanceModeEnabled && terrainProfileOffsets.some(offset => offset.active)){
		await createTerrainOffsets()
	}
	toggleAviationObstaclesReferences();
	
	return referenceList;
};

/**
 * Toggles visibility of aviation obstacles based on referenced entity visibility.
 * 
 * @param showLiveObjects boolean. Defaults to false, but should be true if we want to show only live
 */
const toggleAviationObstaclesReferences = (showLiveObjects = false) => {
	
	const { mapdataDataSource, aviationObstacleDataSource } = useDataSources();
	try{
		aviationObstacleDataSource.entities.values.forEach((aviationObstacleEntity) => {
			const aviationReferencePropertyBag =
				getEntityPropertyBag<IAviationObstaclePropertyBagModel>(aviationObstacleEntity);
			if (
				aviationReferencePropertyBag &&
				aviationReferencePropertyBag.entityType === 'AVIATION_OBSTACLE'
			) {
				const aviationObstaclePropertyBag =
					aviationReferencePropertyBag as IAviationObstaclePropertyBagModel;
				if (!aviationObstaclePropertyBag) return;

				const infrastructureObjects = aviationObstaclePropertyBag.data.infrastructureObjectIds.map((referenceId) => {
					const entity = mapdataDataSource.entities.getById(
						referenceId
					);
					if (!entity) return null;
					return entity;
				}).filter((v) => v !== null) as Entity[];
				

				// Hide aviation obstacles if corresponding map layer is not activated
				if (
					isAviationObstacle('RED', aviationObstaclePropertyBag) &&
					!get(mapLayersStore).aviationObstaclesRedDisplay
				) {
					aviationObstacleEntity.show = false;
					return;
				} else if (
					isAviationObstacle('BLUE', aviationObstaclePropertyBag) &&
					!get(mapLayersStore).aviationObstaclesBlueDisplay
				) {
					aviationObstacleEntity.show = false;
					return;
				} else {
					aviationObstacleEntity.show = true;
				}

				if (infrastructureObjects.length && infrastructureObjects.every((v) => !v.show)) {
					// Hide aviation obstacles if reference entity is hidden
					aviationObstacleEntity.show = false;
				} else if(!infrastructureObjects.length){
					// We need to check the current plan as well. In case some entities are deleted from plan, but still exists in dataset
					const planDataSource = get(mapModeStore).activeDataSource;

					if(planDataSource){
						const planInfrastructureObject = aviationObstaclePropertyBag.data.infrastructureObjectIds.map((referenceId) => {
							const entity = planDataSource.entities.getById(
								referenceId
							);
							if (!entity) return null;
							return entity;
						}).filter((v) => v !== null) as Entity[];
						
						if(planInfrastructureObject.length && planInfrastructureObject.every((v) => !v.show)){
							aviationObstacleEntity.show = false;
						}
					}
				}
				// Don't continue unless we ONLY want to show live objects
				if(!showLiveObjects) return;

				// Handle showing only live objects. Hide plan and show live aviation obstacles that are already hidden
				// Shortcut to hide all aviation obstacles NOT related to live
				if(!infrastructureObjects.length){
					aviationObstacleEntity.show = false;
				}
				// Shortcut to show all aviation obstacles related to live
				if(infrastructureObjects.length){
					aviationObstacleEntity.show = true;
				}
			}
		});
	} catch(e) {
		if(e instanceof Error){
			console.error(e.stack);
			toast({
				type: 'error',
				title: 'Feil ved oppdatering av luftfartshindere',
				description: `Noe gikk feil ved oppdatering av luftfartshindere i kartet. Feilmelding: ${e.message}`
			});
		}
	}
}

const removePlanAviationObstacles = () => {
	
	const { mapdataDataSource, aviationObstacleDataSource } = useDataSources();

	aviationObstacleDataSource.entities.values.forEach((aviationObstacleEntity) => {
		const aviationReferencePropertyBag =
			getEntityPropertyBag<IAviationObstaclePropertyBagModel>(aviationObstacleEntity);
		if (
			aviationReferencePropertyBag &&
			aviationReferencePropertyBag.entityType === 'AVIATION_OBSTACLE'
		) {
			const infrastructureObjects = aviationReferencePropertyBag.data.infrastructureObjectIds.map((referenceId) => {
				const entity = mapdataDataSource.entities.getById(
					referenceId
				);
				if (!entity) return null;
				return entity;
			}).filter((v) => v !== null) as Entity[];

			if(!infrastructureObjects.length){
				aviationObstacleDataSource.entities.remove(aviationObstacleEntity);
			}
			// Shortcut to show all aviation obstacles related to live
			if(infrastructureObjects.length){
				aviationObstacleEntity.show = true;
			}
		}
	})
}

function isAviationObstacle(
	type: 'BLUE' | 'RED',
	aviationObstaclePropertyBag: IAviationObstaclePropertyBagModel
) {
	return (
		(type === 'RED' && aviationObstaclePropertyBag.data.highestDistanceFromGround >= 15) ||
		(type === 'BLUE' && aviationObstaclePropertyBag.data.highestDistanceFromGround < 15)
	);
}

const findDataSourceByCzml = (czml: any[]): CzmlDataSource | null => {
	const { mapdataDataSource } = useDataSources();
	const viewer = useCesiumViewer();

	if (!Array.isArray(czml)) {
		console.error('Parsing error: Not valid czml. Not an array');
		return null;
	}
	const documentMeta = czml.find((czmlItem) => czmlItem.id === 'document');
	if (!documentMeta) {
		console.log(czml);
		console.trace(documentMeta);
		console.error('Parsing error: Not valid czml. No document meta data');
		return null;
	}
	if (documentMeta.name === 'Mapdata') {
		return mapdataDataSource;
	}
	const planDataSource = viewer.dataSources.getByName(documentMeta.name);
	if (planDataSource && planDataSource.length == 1) {
		return planDataSource[0] as CzmlDataSource;
	} else {
		console.error('Parsing error: Less or more than one datasource by the same name');
		return null;
	}
};

/**
 * Retrieves map data for the selected project and displays it in the map
 *
 * @param currentlyHighlightedProjectAndPlan
 */
const openSelectedProjectInMap = async ({
	currentlyHighlightedProjectAndPlan
}: {
	currentlyHighlightedProjectAndPlan: {
		project: {
			id?: number;
			name?: string;
		};
		plan: {
			id?: number;
			name?: string;
		};
	};
}) => {
	const { sandbox_polygonDataSource } = useDataSources();
	const viewer = useCesiumViewer();
	const sceneCenter = undefined;

	mapModeStore.update((store) => {
		store.selectedProjectAndPlan.project = { ...currentlyHighlightedProjectAndPlan.project };
		store.selectedProjectData = undefined;
		return store;
	});

	resetPlanDataSources();
	try {
		mapToolsStore.resetMapToolsStore(viewer);

		if (currentlyHighlightedProjectAndPlan.project.id !== undefined) {
			busyStateMessage.set(
				`Laster inn kartinnhold for planer i <b>${currentlyHighlightedProjectAndPlan.project.name}</b>`
			);
			const selectedProjectData = await api
				.get(`Projects/${get(mapModeStore).selectedProjectAndPlan.project.id}/plans`)
				.json<ISelectedProject>();

			mapModeStore.update((store) => {
				store.expandableProjectPickerCurrentView = 'MINIMIZED';
				store.selectedProjectAndPlan.plan = {
					id: selectedProjectData.plans[selectedProjectData.plans.length - 1].id,
					name: selectedProjectData.plans[selectedProjectData.plans.length - 1].name
				};
				store.selectedProjectData = selectedProjectData;
				return store;
			});

			sandbox_polygonDataSource.entities.removeAll();

			if (!selectedProjectData.plans.length) {
				toast({
					type: 'warning',
					title: 'Ingen planer i prosjekt',
					description: `Prosjektet ${selectedProjectData.name} inneholder ingen planer`
				});
				busyStateMessage.set('');
			}

			await showAllLiveObjects();
			for (let i = 0; selectedProjectData.plans.length > i; i++) {
				busyStateMessage.set(
					`Laster inn data for <b>plan ${i + 1} / ${selectedProjectData.plans.length}</b>`
				);
				let readyToFly = i == selectedProjectData.plans.length - 1;
				await createPlanInstances(selectedProjectData.plans[i], selectedProjectData, readyToFly);
				if (readyToFly) {
					await fetchEntitiesInArea({ sceneCenter, reloadArea: true });
				}
			}

			toast({
				type: 'success',
				title: 'Prosjekt lastet',
				description: `Lastet inn ${selectedProjectData.plans.length} plan${
					selectedProjectData.plans.length > 1 ? 'er' : ''
				}`
			});
			busyStateMessage.set('');
		}
	} catch (error) {
		if (error instanceof RequestErrorEvent) {
			handleUnauthorizedResourceFetch(error);
		} else if (error instanceof HTTPError) {
			// Ky
			toast({
				type: 'error',
				title: 'Feil ved lasting av prosjekt',
				description: `Noe gikk galt ved lasting av prosjekt. Feilmelding: ${error.message}`
			});
		}
		busyStateMessage.set('');
	}
};

const findActiveDrawingDataSources = () => {
	const viewer = useCesiumViewer();
	let activeDrawingDatasources: string[] = [];
	for (let i = 0; i < viewer.dataSources.length; i++) {
		const datasourceName = viewer.dataSources.get(i).name;
		if (datasourceName.includes('planDrawing:')) {
			activeDrawingDatasources = [...activeDrawingDatasources, datasourceName];
		}
	}
	return activeDrawingDatasources;
}

const createPlanDrawingIdentifier = (drawingId: number): `planDrawing:${number}` => {
	return `planDrawing:${drawingId}`;
}

/**
 * Create and populate a datasource for drawings
 * @param drawingId 
 */
const createPlanDrawingDataSource = (drawing: Partial<IPlanDrawing> & {czmlDrawings: any[], planDrawingId: number}) => {
	const viewer = useCesiumViewer();
	const { czmlDrawings } = drawing;
	const identifier = createPlanDrawingIdentifier(drawing.planDrawingId);
	const reformattedCzml = czmlDrawings.map((item) => {
		if (item.id == 'document') {
			return {
				id: 'document',
				name: identifier,
				version: '1.0'
			};
		} else {
			return item;
		}
	}).filter((item) => {
		//Drawings may contain some unwanted entities.
		if(item.properties?.entityType && item.properties.entityType === "POLYGON_PART") return false;
		return true;
	});	
	
	const datasource = new CzmlDataSource(identifier);
	datasource.process(reformattedCzml);
	viewer.dataSources.add(datasource);

}

/**
 * Creates Cesium meta entities, adds datasource and updates store when opening a plan or project
 *
 * @param selectedPlanData Information about a specific plan
 * @param readyToFly Boolean. Decides if this is the last plan in a collection that is processed, and if we should move the camera and color entities.
 *
 */
const createPlanInstances = async (
	selectedPlanData: ISelectedProject['plans'][number],
	selectedProjectData: ISelectedProject,
	readyToFly: boolean = true
) => {
	const viewer = useCesiumViewer();
	const dataSourceToUse = new CzmlDataSource('planId:' + selectedPlanData.id);
	await viewer.dataSources.add(dataSourceToUse);

	const newProjectPolygonId = `ProjectPolygon:${selectedProjectData.id}`;
	const projectPolygonPositions = Cartesian3.fromDegreesArray(
		selectedProjectData.area.coordinates[0].flat()
	);
	const projectPolygon = createPolygonEntity(newProjectPolygonId, projectPolygonPositions);
	dataSourceToUse.entities.add(projectPolygon);
	
	const polygonEntity = dataSourceToUse.entities.add(
		new Entity({
			id: `sandbox_polygon:${selectedPlanData.id}`,
			name: selectedPlanData.name,
			polygon: new PolygonGraphics({
				hierarchy: new PolygonHierarchy(
					Cartesian3.fromDegreesArray(selectedPlanData.area.coordinates[0].flat(1))
				),
				material: Color.BLANCHEDALMOND.withAlpha(0.4),
				heightReference: HeightReference.CLAMP_TO_GROUND
			})
		})
	);

	//TO DO: Restructure to set store values after outer loop to avoid repetetive update of store.
	mapModeStore.update((store) => {
		if (readyToFly) {
			store.activeDataSource = dataSourceToUse;
		}
		store.displayedDataSources = [
			...store.displayedDataSources,
			dataSourceToUse.name as `planId:${number}`
		];
		return store;
	});

	if (!readyToFly) return;

	await dataSourceColorPicker();
	await viewer.flyTo(polygonEntity, {
		offset: new HeadingPitchRange(0, -CesiumMath.PI_OVER_TWO, 180),
		duration: 3
	});
};

/**
 * Triggered when opening a new plan from ExpandablePlanPicker.
 *
 * @param currentlyHighlightedProjectAndPlan Project and plan which is marked as selected in the UI
 * @param activePlan Needed for passing a value to not display warning message on plan creation
 *
 * @returns void
 */
const openNewPlan = async (
	currentlyHighlightedProjectAndPlan: {
		project?: {
			id?: number;
			name?: string;
		};
		plan?: {
			id?: number;
			name?: string;
		};
	},
	activePlan: boolean
) => {
	const { sandbox_polygonDataSource } = useDataSources();
	const viewer = useCesiumViewer();
	const currentMapModeStore = get(mapModeStore);
	const sceneCenter = undefined;
	try {
		/** Trenger bare kjøre på siste planen i prosjekt */
		if (activePlan) {
			mapModeStore.update((store) => {
				store.expandableProjectPickerCurrentView = 'MINIMIZED';
				store.selectedProjectAndPlan.plan = { ...currentlyHighlightedProjectAndPlan.plan };
				store.selectedProjectAndPlan.project = {
					...currentlyHighlightedProjectAndPlan.project
				};
				store.selectedProjectData = undefined;
				return store;
			});
		}
		//Initialize state for opening a new plan
		resetPlanDataSources();
		mapToolsStore.resetMapToolsStore(viewer);

		if (currentMapModeStore.selectedProjectAndPlan.plan.id === undefined) {
			return;
		}

		busyStateMessage.set(
			`Laster kartinnhold for plan<br/> <b>${currentMapModeStore.selectedProjectAndPlan.plan.name}</b>`
		);

		const selectedProjectData = await api
			.get(`Projects/${currentMapModeStore.selectedProjectAndPlan.project.id}/plans`)
			.json<ISelectedProject>();

		const selectedPlanData = selectedProjectData.plans.find((plan) => {
			return plan.id == currentMapModeStore.selectedProjectAndPlan.plan.id;
		});

		sandbox_polygonDataSource.entities.removeAll();

		if (selectedPlanData) {
			mapModeStore.update((store) => {
				if(store.mode !== 'SANDBOX'){
					store.mode = 'SANDBOX';
				}
				store.selectedProjectData = {
					...selectedProjectData,
					plans: [selectedPlanData]
				};
				return store;
			});
			//await showAllLiveObjects();
			await createPlanInstances(selectedPlanData, selectedProjectData);
			if (activePlan) {
				tick();
				await fetchEntitiesInArea({ sceneCenter, reloadArea: true });
			}
			busyStateMessage.set('');
		} else {
			//$mapModeStore.expandableProjectPickerCurrentView = 'PROJECTS';
			busyStateMessage.set(
				`Planen <b>${currentMapModeStore.selectedProjectAndPlan.plan.name}</b> har ingen kartdata å visualisere.`
			);
			await new Promise((r) => setTimeout(r, 2500));
			busyStateMessage.set('');
		}

		busyStateMessage.set('');
	} catch (error) {
		if (error instanceof RequestErrorEvent) {
			handleUnauthorizedResourceFetch(error);
		} else if (error instanceof HTTPError) {
			// Ky
			toast({
				type: 'error',
				title: 'Feil ved lasting av plan',
				description: `Noe gikk galt ved lasting av plan. Feilmelding: ${error.message}`
			})
		}
		busyStateMessage.set('');
	}
};

/**
 * Updates the store holding plandata. Removes any custom datasources the store holds. Both plans and sandbox.
 */
const resetPlanDataSources = () => {
	const viewer = useCesiumViewer();
	showAllLiveObjects();

	mapModeStore.update((store) => {
		if (!store.displayedDataSources.length || typeof store.displayedDataSources[0] == undefined)
			return store;
		//Reset temporary datasources
		for (let i = 0; store.displayedDataSources.length > i; i++) {
			const tempDataSourceToRemove = viewer.dataSources.getByName(store.displayedDataSources[i]);

			tempDataSourceToRemove.map((dataSourceItem) => {
				if (viewer.dataSources.contains(dataSourceItem)) {
					viewer.dataSources.remove(dataSourceItem, false);
				}
			});
		}
		//Reset states
		store.displayedDataSources = [];
		store.activeDataSource = undefined;

		return store;
	});
};



/**
 * A utility for processing czml-data both as json and an array of json.
 * Loads and handles updates to czml-data in Cesium.
 *
 * @param czmlResponse A Czml json-response or an array of czml json-response
 * @param toggleLive Wether or not to affect live data. Default true
 */
const processPlanCzml = async (czmlResponse: any[], toggleLive = true) => {
	const { mapdataDataSource } = useDataSources();

	const processSingleDatasource = async (czmlDocument: any[]) => {
		const dataSource = findDataSourceByCzml(czmlDocument);
		if (dataSource && dataSource !== mapdataDataSource) {
			await dataSource.process(czmlDocument);
			if (toggleLive) await handleLiveReferences(dataSource.entities.values);
			return;
		}
		await mapdataDataSource.process(czmlDocument);
	}
	// Check if first item is an array. We may get an array of czml documents
	if (Array.isArray(czmlResponse[0])) {
		czmlResponse.forEach(async (czmlDocument: any[]) => {
			await processSingleDatasource(czmlDocument);
		});
	} else {
		await processSingleDatasource(czmlResponse);
	}
};

const publishPlansByPlanId = async (planId: number | undefined) => {
	const { mapdataDataSource } = useDataSources();
	if (!planId) {
		toast({
			type: 'error',
			title: 'Kunne ikke publisere plan',
			description: `Planen du prøver å publisere finnes ikke lenger. Velg planen på nytt og prøv igjen.`
		});
		mapModeStore.setInitialProjectsAndPlansState('MINIMIZED');
		return;
	}

    confirmModal(
        'Er du sikker på at du vil publisere planen? Handlingen kan ikke angres',
		async () => {
			busyStateMessage.set('Publiserer plan');
			mapModeStore.update((store) => {
				store.expandableProjectPickerCurrentView = 'MINIMIZED';
				return store;
			});

			try {
				const publishPlans = await api.put(`Plans/publish/${planId}`);

				if (publishPlans.ok) {
					mapdataDataSource.entities.removeAll();

					/*const fetchAllMapDataAbortController = new AbortController();
					await fetchMapData({
						signal: fetchAllMapDataAbortController.signal
					});*/

					resetPlanDataSources();
					mapModeStore.reset();

					await fetchEntitiesInArea({ sceneCenter: undefined, reloadArea: true });

					if (mapdataDataSource?.entities?.values?.length > 0) {
						// If the logged in user is connected to a dataset with data, visualize it.
						busyStateMessage.set(
							`Oppdaterer med nytt kartinnhold <i>(${mapdataDataSource.entities.values.length})</i>`
						);
					}
					toast({
						type: 'success',
						title: 'Plan publisert',
						description: `Planen er nå publisert og endringene er synlige for andre brukere.`
					})
				} else {
					toast({
						type: 'error',
						title: 'Feil ved publisering av plan',
						description: `Noe gikk galt ved publisering av plan. Feilmelding: ${publishPlans.statusText}`
					})
				}

				busyStateMessage.set('');
			} catch (error) {
				if (error instanceof RequestErrorEvent) {
					handleUnauthorizedResourceFetch(error);
				} else if (error instanceof HTTPError) {
					// Ky
					toast({
						type: 'error',
						title: 'Feil ved publisering av plan',
						description: `Noe gikk galt ved publisering av plan. Feilmelding: ${error.message}`
					})
				}
				busyStateMessage.set('');
			}
		},
		() => {},
		{
			labels: {
				title: 'Publisering av plan'
			}
		}
    )
};

/**
 * Takes an entity and checks which Datasource it belongs to. Simplified!
 *
 * @param entity The entity the check for
 * @returns CzmlDataSource
 */
const findDataSourceByEntity = (entity: Entity): CzmlDataSource | null => {
	return entity.entityCollection.owner as CzmlDataSource;
};

/**
 * In some cases we would like to know which datasource an arbitrary entity belongs to.
 *
 * @param entityId a string containing an identifier for the entity. Composed by type and externalId. Example: MastPoint:<uuid>
 * @returns Entity | null
 */
const findEntityByIdBetweenDataSources = (entityId: string): Entity | null => {
	const { mapdataDataSource } = useDataSources();
	const viewer = useCesiumViewer();
	const activeDataSource = get(mapModeStore).activeDataSource;
	const displayedDataSources = get(mapModeStore).displayedDataSources;

	let stopSearch = false;
	let entityToReturn = null;
	if (displayedDataSources.length) {
		let dataSourceCollection: (CzmlDataSource | DataSource | undefined)[] = [
			activeDataSource,
			mapdataDataSource
		];

		for (let dataSourceName of displayedDataSources) {
			const retrievedDataSourceList = viewer.dataSources.getByName(dataSourceName);

			retrievedDataSourceList.forEach((retrievedDataSource) => {
				if (dataSourceCollection.includes(retrievedDataSource)) return;
				dataSourceCollection = [...dataSourceCollection, retrievedDataSource];
			});
		};
		for(let dataSource of dataSourceCollection) {
			if (dataSource) {
				const EntityInsideDataSource = dataSource.entities.getById(entityId);
				if (EntityInsideDataSource) {
					return EntityInsideDataSource;
				}
			} else {
				console.warn(`No datasources in collection when traversing datasources for entities`);
			}
		};
		return entityToReturn;
	} else {
		const insideLive =
			mapdataDataSource.entities.getById(entityId) && mapdataDataSource.entities.getById(entityId);
		if (insideLive) {
			return insideLive;
		} else {
			console.error(`Couldn't retrieve entity. No datasources hold ${entityId}`);
			return null;
		}
	}
};

/**
 * Used to actively change color of a mastpoint. Actively responds to current state of map settings to decide color for a single entity.
 * 
 * @param entity - The entity to color
 * @param dataSource - The datasource the entity belongs to
 * @param partnerColorsActive - Is the setting enabled? default false
 * @param partnerCompanyColors - A Map collection of partner companies. 
 * @returns void
 */
const mastPointColorPicker = (
		entity: Entity, 
		dataSource: CzmlDataSource, 
		partnerColorsActive: boolean = false, 
		partnerCompanyColors: Map<number, string> | undefined = undefined,
		showPartnerEntities: boolean = true
	) => {
	// Mastpoint entity without cylinder properties
	if (!entity.cylinder) return;
	const MASTPOINT_BASE_COLOR = new Color(220/255,220/255,220/255,1);
	const MASTPOINT_HIDDEN_OPACITY = 0.1;

	const activeDataSource = get(mapModeStore).activeDataSource;
	const { mapdataDataSource } = useDataSources();
	// When multiple datasources are introduced, inactive datasources should have color with opacity.
	const inactiveOpacity = 0.4;
	const entityOwner: number = entity.properties?.getValue(new JulianDate).data?.ownerCompanyId;
	const partnerColor = partnerCompanyColors?.get(entityOwner);
	// If there is a match with ownerId and partnercompanies, we can consider the entity a partner entity
	const isPartnerEntity = partnerColor && typeof partnerColor !== "undefined";
	const isCompanyEntity = entityOwner === get(userAccountStore).companyId;
	const opacity = !showPartnerEntities && !isCompanyEntity ? MASTPOINT_HIDDEN_OPACITY : 1;
	//RGBA values are divided by 255 as Color values range from 0 to 1

	// If partner colors are activated and there is a match with registered partners.
	if(partnerColorsActive && isPartnerEntity){
		//Partner color - Dynamic
		entity.cylinder.material = setPartnerColorProperty(partnerColor, opacity);
		return;
	}  else if (partnerColorsActive && !isPartnerEntity && !isCompanyEntity) {
		// Is not a registered partner or company Entity - INDIANRED
		entity.cylinder.material = setColorProperty(Color.INDIANRED.withAlpha(opacity));
		return;
	}else if (dataSource === activeDataSource && !partnerColorsActive) {
		//Active plan - Purple
		entity.cylinder.material = setColorProperty(ACTIVE_PLAN_COLOR.withAlpha(opacity));

		return;
	} else if (dataSource !== mapdataDataSource && dataSource !== activeDataSource) {
		//Inactive plan - Gray
		entity.cylinder.material = setColorProperty(INACTIVE_PLAN_COLOR.withAlpha(inactiveOpacity));
		return;
	} else {
		entity.cylinder.material = setColorProperty(MASTPOINT_BASE_COLOR.withAlpha(opacity));
		return;
	}
};

/**
 * Used to actively change color of a mastpoint. Actively responds to current state of map settings to decide color for a single entity.
 * 
 * @param entity - The entity to color
 * @param dataSource - The datasource the entity belongs to
 * @param partnerColorsActive - Is the setting enabled? default false
 * @param partnerCompanyColors - A Map collection of partner companies. 
 * @returns void
 */
const poleColorPicker = (
		entity: Entity, 
		dataSource: CzmlDataSource, 
		partnerColorsActive: boolean = false, 
		partnerCompanyColors: Map<number, string> | undefined = undefined,
		showPartnerEntities: boolean = true
	) => {
	// Pole entity without cylinder properties
	if (!entity.cylinder) return;
	const POLE_BASE_COLOR = new Color(220/255,220/255,220/255,1);
	const POLE_HIDDEN_OPACITY = 0.1;

	const activeDataSource = get(mapModeStore).activeDataSource;
	const { mapdataDataSource } = useDataSources();
	// When multiple datasources are introduced, inactive datasources should have color with opacity.
	const inactiveOpacity = 0.4;
	const entityOwner: number = entity.parent?.properties?.getValue(new JulianDate).data?.ownerCompanyId;
	const partnerColor = partnerCompanyColors?.get(entityOwner);
	// If there is a match with ownerId and partnercompanies, we can consider the entity a partner entity
	const isPartnerEntity = partnerColor && typeof partnerColor !== "undefined" ? true : false;
	const isCompanyEntity = entityOwner === get(userAccountStore).companyId;
	const opacity = !showPartnerEntities && !isCompanyEntity ? POLE_HIDDEN_OPACITY : 1;
	//RGBA values are divided by 255 as Color values range from 0 to 1

	// If partner colors are activated and there is a match with registered partners.
	if(partnerColorsActive && isPartnerEntity && partnerColor){
		//Partner color - Dynamic
		entity.cylinder.material = setPartnerColorProperty(partnerColor, opacity);
		return;
	}  else if (partnerColorsActive && !isPartnerEntity && !isCompanyEntity) {
		// Is not a registered partner or company Entity - INDIANRED
		entity.cylinder.material = setColorProperty(Color.INDIANRED.withAlpha(opacity));
		return;
	}else if (dataSource === activeDataSource && !partnerColorsActive) {
		//Active plan - Purple
		entity.cylinder.material = setColorProperty(ACTIVE_PLAN_COLOR.withAlpha(opacity));

		return;
	} else if (dataSource !== mapdataDataSource && dataSource !== activeDataSource) {
		//Inactive plan - Gray
		entity.cylinder.material = setColorProperty(INACTIVE_PLAN_COLOR.withAlpha(inactiveOpacity));
		return;
	} else {
        // Default color - Gray
		entity.cylinder.material = setColorProperty(POLE_BASE_COLOR.withAlpha(opacity));
		return;
	}
};

const lineColorPicker = (
		entity: Entity, 
		dataSource: CzmlDataSource, 
		partnerColorsActive: boolean = false, 
		partnerCompanyColors: Map<number, string> | undefined = undefined, 
		showPartnerEntities: boolean = true
	) => {
	if(!entity.polyline) return;
	
	//Divided by 255 as Color values range from 0 to 1
	const LINE_BASE_COLOR = new Color(47/255,79/255,79/255,1);
	const LINE_HIDDEN_OPACITY = 0.1;

	const activeDataSource = get(mapModeStore).activeDataSource;
	const entityOwner: number = entity.properties?.getValue(new JulianDate)?.data?.ownerCompanyId;
	const entityIsMerged: boolean = entity.properties?.getValue(new JulianDate)?.data?.isMerged
	const inactiveOpacity = 0.4;
	const partnerColor = partnerCompanyColors?.get(entityOwner);
	const isCompanyEntity = entityOwner === get(userAccountStore).companyId;
	// If there is a match with ownerId and partnercompanies, we can consider the entity a partner entity
	const isPartnerEntity = partnerColor && typeof partnerColor !== "undefined" ? true : false;
	const opacity = !showPartnerEntities && !isCompanyEntity ? LINE_HIDDEN_OPACITY : 1;
    
	if(partnerColorsActive && isPartnerEntity && partnerColor){
		entity.polyline.material = setPartnerColorProperty(partnerColor, opacity);
		return;
	} else if (partnerColorsActive && !isPartnerEntity && !isCompanyEntity) {
		// Is not a registered partner or company Entity - INDIANRED
		entity.polyline.material = setColorProperty(Color.INDIANRED.withAlpha(opacity));
		return;
	}else if(entityIsMerged){
		entity.polyline.material = setColorProperty(Color.DEEPSKYBLUE.withAlpha(opacity))
	}  else if (dataSource === activeDataSource && !partnerColorsActive) {
		entity.polyline.material = setColorProperty(
			ACTIVE_PLAN_COLOR.withAlpha(opacity).darken(0.6, new Color())
		);
	} else {
		entity.polyline.material = setColorProperty(LINE_BASE_COLOR.withAlpha(opacity));
	}
}

const supportColorPicker = (
	entity: Entity, 
	dataSource: CzmlDataSource, 
	partnerColorsActive: boolean = false, 
	partnerCompanyColors: Map<number, string> | undefined = undefined,
	showPartnerEntities: boolean = true
) => {
	if(!entity.polyline) return;

	/*if(!showPartnerEntities){
		entity.show = false;
		return
	}else{
		entity.show = true;
	}*/
	if(entity.id.startsWith('supportPoleId:')){
		// If support is B (Bardun)
		const supportMaterialProperties = entity.polyline.material.getValue(
			new JulianDate()
		);
		const actualColor: Color = supportMaterialProperties.color;
		const { dashLength, dashPattern, gapColor } = supportMaterialProperties;

		entity.polyline.material = new PolylineDashMaterialProperty({
			color: new Color(actualColor.red, actualColor.green, actualColor.blue),
			dashLength,
			dashPattern,
			gapColor
		});
	} else if (entity.id.startsWith('support:')){
		// If support is S (Strever)
		const supportMaterialProperties = entity.polyline.material.getValue(
			new JulianDate()
		);
		const actualColor: Color = supportMaterialProperties.color;

		entity.polyline.material = setColorProperty(
			new Color(actualColor.red, actualColor.green, actualColor.blue)
		);
	}
}

/**
 * Handles coloring of specific entities in plans.
 *
 * @param entity A single entity to recolor
 */

const entityColorPicker = async (entity: Entity, partnerCompanyColors: Map<number, string> | undefined = undefined, partnerColorsActive: boolean = false, showPartnerEntities: boolean = true) => {
	const ownerDatasource = entity.entityCollection.owner as CzmlDataSource;
	if (entity.cylinder && entity.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) {
		// MASTPOINT (OLD)
		mastPointColorPicker(entity, ownerDatasource, partnerColorsActive, partnerCompanyColors, showPartnerEntities);
	} else if (entity.polyline && entity.id.startsWith(ENTITY_ID_PREFIX.LINE)) {
		// LINE
		lineColorPicker(entity, ownerDatasource, partnerColorsActive, partnerCompanyColors, showPartnerEntities);
	} else if (entity.cylinder && entity.id.startsWith(ENTITY_ID_PREFIX.POLE)) {
        // POLE
        poleColorPicker(entity, ownerDatasource, partnerColorsActive, partnerCompanyColors, showPartnerEntities);
    }else if ((entity.id.includes('supportPoleId') || entity.id.includes('support:')) && entity.polyline) {
		// SUPPORT
		supportColorPicker(entity, ownerDatasource, partnerColorsActive, partnerCompanyColors, showPartnerEntities)
	} else if (entity.id.includes('sandbox_polygon') && entity.polygon) {
		const polygonColor = Color.BLANCHEDALMOND;
		//Polygons should always have a lower opacity than entities.
		const polygonOpacity = get(mapModeStore).settings.planPolygonOpacity;
		entity.polygon.material = setColorProperty(polygonColor.withAlpha(polygonOpacity))
	};
}

/**
 * Handles coloring of entities by datasource. Recolors all entities in displayed datasources when in project/plan mode.
 *
 * @param dataSource Since both sandbox and live has an undefined planId, we pass the predefined "mapdataDataSource" in case of live.
 */
const dataSourceColorPicker = async (dataSource?: CzmlDataSource) => {
	const {showPartnerEntities, partnerCompanies, enablePartnerColors} = get(mapSettingsState);
	//const userCompanyId = get(userAccountStore).companyId;
	if (!dataSource) {
		const activeDataSource = get(mapModeStore).activeDataSource;
		const entitiesToModify = activeDataSource?.entities.values;

		//Handle entities in plan that is active
		if (entitiesToModify && entitiesToModify.length) {
			// Handles color for each individual entity in active datasource
			entitiesToModify.forEach(entity => entityColorPicker(entity, partnerCompanies, enablePartnerColors, showPartnerEntities));
		}
	} else if (dataSource) {
		//LIVE is visually modified
		const entitiesToModify = dataSource.entities.values;

		if (entitiesToModify) {
			entitiesToModify.forEach(entity => entityColorPicker(entity, partnerCompanies, enablePartnerColors, showPartnerEntities));
		}
	}
};

export {
	handleDistanceDisplay,
	openSelectedProjectInMap,
	openNewPlan,
	dataSourceColorPicker,
	resetPlanDataSources,
	publishPlansByPlanId,
	findDataSourceByEntity,
	findEntityByIdBetweenDataSources,
	processPlanCzml,
	findDataSourceByCzml,
	mastPointColorPicker,
	handleLiveReferences,
	showAllLiveObjects,
	entityColorPicker,
	toggleAviationObstaclesReferences,
	removePlanAviationObstacles,
	createPlanDrawingDataSource,
	createPlanDrawingIdentifier,
	findActiveDrawingDataSources
};
